/* eslint-disable max-len */
import {
  useTranslate,
  FunctionField,
  ExportButton,
  useGetList,
  useList,
  ListContextProvider,
  List,
  useSidebarState
} from "react-admin";
import { useParams, Link, useLocation } from "react-router-dom";
import CustomDataGrid from "./CustomDataGrid";
import { DownloadIcon } from "assets/Icons";
import dayjs from "dayjs";
import * as User from "providers/user";
import { Theme, useMediaQuery } from "@mui/material";
import { CustomButton } from "./CustomButton";
import { getZonedDate } from "../utils/helpers";

export default function UserAppointmentsList() {
  const { id } = useParams<{ id: string }>();
  const translate = useTranslate();
  const { permissions } = User.useUser();
  const router = useLocation();
  const [open] = useSidebarState();
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  const isMobileBreakPoint = useMediaQuery<Theme>("(max-width:640px)");
  const isMedium = useMediaQuery<Theme>("(max-width:1024px)");
  const isIpadSizeScreen = isMedium && !isMobileBreakPoint;
  const resource = router.pathname.split("/")[2];

  const { data, isLoading } = useGetList("Appointment", {
    filter: {
      user_id: id,
      is_blocked_time: {
        equals: false
      },
      ...(permissions?.isSuperAdmin && resource === "User"
        ? {}
        : { location: { is: { company_id: permissions.companyId } } })
    },
    sort: { field: "start_time", order: "DESC" },
    pagination: { page: 1, perPage: 100 }
  });
  const listContext = useList({ data, isLoading });

  const handleStatus = (recordItem: any) => {
    if (recordItem?.rescheduled_at) {
      return (
        <div className="text-[#af6ef5]">
          {translate("appointment.rescheduled")}
        </div>
      );
    }
    if (recordItem?.cancelled_at) {
      return (
        <div className="text-error">{translate("appointment.cancelled")}</div>
      );
    }
    if (
      dayjs().isBefore(
        dayjs(
          getZonedDate(recordItem?.start_time, recordItem?.location?.timezone)
        )
      )
    ) {
      return (
        <div className="text-mono-primary">
          {translate("appointment.upcoming")}
        </div>
      );
    }
    if (
      dayjs().isAfter(
        dayjs(
          getZonedDate(recordItem?.end_time, recordItem?.location?.timezone)
        )
      )
    ) {
      return <div>{translate("appointment.completed")}</div>;
    }
    if (
      dayjs(
        getZonedDate(recordItem?.start_time, recordItem?.location?.timezone)
      ).isAfter(dayjs()) &&
      dayjs().isBefore(
        dayjs(
          getZonedDate(recordItem?.end_time, recordItem?.location?.timezone)
        )
      )
    ) {
      return (
        <div className="text-mono-primary">
          {translate("appointment.in_progress")}
        </div>
      );
    }
    return <div>-</div>;
  };

  return (
    <List
      empty={
        <div className="text-[14px] font-['Inter'] font-[400] text-mono-primaryTextColor">
          {translate("users.no_appointment_yet")}
        </div>
      }
      resource="Appointment"
      filter={{ user_id: id }}
      actions={
        <div className="h-0 relative">
          <div className="absolute -top-[50px] right-0 z-10 customListActionsButtonsWrapper">
            <ExportButton
              label={translate("common.export_to_csv")}
              icon={<DownloadIcon />}
            />
          </div>
        </div>
      }
      sx={{
        marginTop: "4px",
        "& .RaList-main > div": {
          minHeight: "0px"
        },
        "& .MuiTablePagination-root": {
          display: "none"
        },
        ".customListActionsButtonsWrapper .MuiButton-sizeSmall": {
          width: "max-content",
          height: "36px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#FAF9F7",
          color: "rgba(48, 47, 46, 0.95)",
          fontSize: "14px",
          lineHeight: "18px",
          fontWeight: 500,
          borderRadius: "18px",
          padding: "0px 16px",
          border: "1px solid rgba(48, 47, 46, 0.23)",
          textTransform: "none"
        },
        ".RaDatagrid-tableWrapper": {
          width: isMobileBreakPoint
            ? null
            : !open
            ? `calc(100vw - 150px)`
            : `calc(100vw - ${isIpadSizeScreen ? 274 : 334}px)`,
          overflowX: "auto"
        }
      }}
    >
      <ListContextProvider value={listContext}>
        <CustomDataGrid
          rowClick={
            isSmall
              ? false
              : (appointmentId: string) =>
                  `/dashboard/Appointment/${appointmentId}/show`
          }
          {...(!isSmall
            ? {
                sx: {
                  borderRadius: "0px",
                  ".RaDatagrid-headerCell": {
                    backgroundColor: "transparent",
                    fontSize: "14px",
                    padding: isIpadSizeScreen ? "12px 8px" : "12px 16px",
                    "@media screen and (max-width:1280px)": {
                      fontSize: "12px"
                    }
                  },
                  ".RaDatagrid-rowCell": {
                    borderBottom: "solid 1px rgba(16, 39, 52, 0.05)",
                    padding: isIpadSizeScreen ? "12px 8px" : "12px 16px"
                  },
                  ".RaDatagrid-rowCell span": {
                    fontSize: "14px",
                    "@media screen and (max-width:1280px)": {
                      fontSize: "12px"
                    }
                  }
                }
              }
            : {})}
        >
          <FunctionField
            label={translate("appointment.persons")}
            data-label={translate("appointment.persons") + ":"}
            sx={{ marginTop: "6px" }}
            render={(record: any) => {
              const uniquePersonsCount = new Set(
                record?.appointment_services?.map(
                  (app: any) => app?.person_number
                )
              )?.size;
              return <div>{uniquePersonsCount}</div>;
            }}
          />
          <FunctionField
            label={translate("appointment.location")}
            data-label={translate("appointment.location") + ":"}
            render={(record: any) => (
              <Link
                onClick={(e) => e.stopPropagation()}
                to={`/dashboard/Location/${record?.location?.id}/show`}
              >
                <span className="line-clamp-2 break-words min-w-[100px] max-w-[120px] xl:max-w-[200px] md:min-w-[70px] xl:min-w-[120px] text-mono-link hover:underline">
                  {record?.location?.nickname || record?.location?.name}
                </span>
              </Link>
            )}
          />
          <FunctionField
            label={translate("appointment.services")}
            data-label={translate("appointment.services") + ":"}
            textAlign="right"
            render={(record: any) => (
              <div>{record?.appointment_services?.length}</div>
            )}
          />
          <FunctionField
            label={translate("common.add_ons")}
            data-label={translate("common.add_ons") + ":"}
            textAlign="right"
            render={(record: any) => {
              const addOnsCount = record?.appointment_services?.reduce(
                (acc: number, curr: any) => {
                  acc += curr?.add_ons?.length;
                  return acc;
                },
                0
              );
              return <div>{addOnsCount > 0 ? addOnsCount : "-"}</div>;
            }}
          />
          <FunctionField
            label={translate("appointment.artists")}
            data-label={translate("appointment.artists") + ":"}
            textAlign="right"
            render={(record: any) => {
              const uniqueArtistsCount = new Set(
                record?.appointment_services?.map((app: any) => app?.artist?.id)
              )?.size;
              return <div>{uniqueArtistsCount}</div>;
            }}
          />
          <FunctionField
            label={translate("appointment.start_time")}
            data-label={translate("appointment.start_time") + ":"}
            textAlign="right"
            render={(record: any) => (
              <div>
                {dayjs(
                  getZonedDate(record?.start_time, record?.location?.timezone)
                ).format("hh:mm A")}
              </div>
            )}
          />
          <FunctionField
            label={translate("appointment.end_time")}
            data-label={translate("appointment.end_time") + ":"}
            textAlign="right"
            render={(record: any) => (
              <div>
                {dayjs(
                  getZonedDate(record?.end_time, record?.location?.timezone)
                ).format("hh:mm A")}
              </div>
            )}
          />
          <FunctionField
            label={translate("appointment.date")}
            data-label={translate("appointment.date") + ":"}
            textAlign="right"
            render={(record: any) => (
              <div className="line-clamp-2 min-w-[100px] md:min-w-[70px] xl:min-w-[120px]">
                {dayjs().isSame(
                  dayjs(
                    getZonedDate(record?.start_time, record?.location?.timezone)
                  ).format("MM/DD/YYYY"),
                  "day"
                )
                  ? translate("appointment.today")
                  : dayjs(
                      getZonedDate(
                        record?.start_time,
                        record?.location?.timezone
                      )
                    ).format("MMM D, YYYY")}
              </div>
            )}
          />
          <FunctionField
            label={translate("order.amount")}
            data-label={translate("order.amount") + ":"}
            textAlign="right"
            render={(record: any) => (
              <div>${record?.Order?.full_amount || record?.full_amount}</div>
            )}
          />
          <FunctionField
            label={translate("appointment.status")}
            data-label={translate("appointment.status") + ":"}
            textAlign="right"
            render={(record: any) => <div>{handleStatus(record)}</div>}
          />
          {isSmall && (
            <FunctionField
              render={(record: any) => (
                <Link
                  to={`/dashboard/Appointment/${record?.id}/show`}
                  style={{ width: "100%" }}
                >
                  <CustomButton
                    variant="secondary"
                    text={translate("common.view_details")}
                    sx={{ width: "100%", marginBottom: "10px" }}
                  />
                </Link>
              )}
            />
          )}
        </CustomDataGrid>
      </ListContextProvider>
    </List>
  );
}
